/**
 * Client for the document-storage BFF (interim doc-storage feature).
 *
 * Mirrors `src/features/auth/management-api.ts`: reads the current ZITADEL
 * access token from the singleton UserManager and attaches it as a Bearer
 * header. The BFF validates that token (issuer/audience/signature/expiry),
 * maps the caller's roles to a tier/collection access matrix, and brokers
 * blob + AI-search access under its own managed identity. The BFF — not the
 * SPA — is the real authorization boundary; the UI gates (see
 * `@/lib/auth/can`) are a UX nicety only.
 *
 * Calls route to `VITE_DOCS_BFF_ORIGIN` (empty = same origin, the common
 * case: prod nginx proxies `/api/*` to the BFF, dev Vite proxy forwards
 * `/api` server-side).
 *
 * Error contract: throws `DocsApiError` with the HTTP status + the message
 * the BFF returned (FastAPI's `{detail}` envelope). Callers catch and toast.
 */

import { env } from "@/env";
import { getCurrentUser } from "@/features/auth/user-manager";
import { actAsClientId } from "@/lib/active-client";

const BASE = env.VITE_DOCS_BFF_ORIGIN.replace(/\/+$/, "");

export class DocsApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "DocsApiError";
  }
}

/**
 * A 412 from the BFF's optimistic-concurrency guard (E1): the blob changed
 * since we last read it (stale `If-Match`), or a create-only write found the
 * blob already present. Callers re-read, merge and retry rather than clobber.
 */
export class DocsConflictError extends DocsApiError {
  constructor(message = "stale — reload before saving") {
    super(412, message);
    this.name = "DocsConflictError";
  }
}

/** Resolve a BFF-relative path against the configured origin (slash-safe). */
function docsUrl(path: string): string {
  return `${BASE}${path}`;
}

/**
 * True when a non-expired ZITADEL access token is available for BFF calls.
 * Used by blob/record sync to soft-skip (stay `idle`) instead of painting
 * "Not signed in" when hydrate or the poll fires before OIDC is ready.
 */
export async function isDocsAuthReady(): Promise<boolean> {
  try {
    const user = await getCurrentUser();
    return Boolean(user?.access_token && !user.expired);
  } catch {
    return false;
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const user = await getCurrentUser();
  if (!user || !user.access_token || user.expired) {
    throw new DocsApiError(401, "Not signed in");
  }
  const headers: Record<string, string> = {
    Authorization: `Bearer ${user.access_token}`,
  };
  // Multi-client: a facilitator's explicit client pick rides every BFF call.
  // Client users get null here (no header — the BFF scopes them from the
  // token and hard-403s a client caller who sends one). This one function is
  // the chokepoint: every BFF fetch in the app goes through it.
  const acting = actAsClientId();
  if (acting) headers["X-Client-Id"] = acting;
  return headers;
}

/** Extract the BFF's error message (FastAPI `{detail}`), falling back to status text. */
async function errorFrom(res: Response): Promise<DocsApiError> {
  let message = res.statusText;
  try {
    const payload = (await res.json()) as { detail?: string };
    if (payload.detail) message = payload.detail;
  } catch {
    /* body not JSON — keep statusText */
  }
  // 412 is the optimistic-concurrency conflict (E1) — give callers a typed
  // subclass to branch on (re-read + merge + retry) vs a generic failure.
  if (res.status === 412) return new DocsConflictError(message);
  return new DocsApiError(res.status, message);
}

async function requestJson<T>(
  method: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(docsUrl(path), {
    ...init,
    method,
    headers: { Accept: "application/json", ...(await authHeader()), ...(init.headers ?? {}) },
  });
  if (!res.ok) throw await errorFrom(res);
  if (res.status === 204) return {} as T;
  return (await res.json()) as T;
}

/* ── Types (the BFF's response shapes, design §4.3) ───────────────────── */

/** A classification tier the caller can see + the actions they may take. */
export interface TierAccess {
  tier: string;
  /** Sorted subset of "read" | "write" | "delete". */
  actions: string[];
}

/** A blob listed under a tier (folders are virtual prefixes in `path`). */
export interface DocBlob {
  path: string;
  size: number;
  last_modified: string | null;
  content_type: string | null;
  /** Strong etag (E1) — seed for the next conditional overwrite's If-Match. */
  etag?: string | null;
}

export interface SearchHit {
  content: string;
  context_tag: string;
  filename: string;
  page: string | number;
  score: number;
  /** Blob path within the tier (stamped at ingest). Empty on older points. */
  filepath?: string;
  /** e.g. `library/sops/policy.pdf` — fallback when `filepath` is absent. */
  document_id?: string;
  /** Tier name (`library` | `restricted` | `workshops`) — download key. */
  classification?: string;
}

/** Known document tiers the BFF brokers. */
const DOC_TIERS = new Set(["library", "restricted", "workshops", "questionnaires"]);

/**
 * Resolve a search hit to the `(tier, path)` pair `downloadDoc` needs.
 * Prefers `filepath` + `classification`; falls back to parsing `document_id`
 * (`tier/rest…`). Returns null when the hit can't be fetched as a blob
 * (caller may still show the snippet as a text-only preview).
 */
export function resolveDocHitLocation(
  hit: Pick<SearchHit, "filepath" | "document_id" | "classification" | "filename">,
): { tier: string; path: string } | null {
  const classification = (hit.classification ?? "").trim();
  const filepath = (hit.filepath ?? "").trim();
  const documentId = (hit.document_id ?? "").trim();

  if (filepath && classification && DOC_TIERS.has(classification)) {
    return { tier: classification, path: filepath };
  }

  if (documentId.includes("/")) {
    const slash = documentId.indexOf("/");
    const tier = documentId.slice(0, slash);
    const rest = documentId.slice(slash + 1);
    if (DOC_TIERS.has(tier) && rest) {
      return { tier, path: filepath || rest };
    }
  }

  // Flat filename + known tier (common for library roots).
  const name = (hit.filename ?? "").trim();
  if (name && classification && DOC_TIERS.has(classification)) {
    return { tier: classification, path: filepath || name };
  }

  return null;
}

export interface SearchResponse {
  results: SearchHit[];
  /** Collections actually queried — reflects the caller's readable tiers. */
  collections_searched: string[];
}

/* ── Endpoints (design §4.3) ──────────────────────────────────────────── */

/** Tiers the caller can see, with their permitted actions. */
export async function listTiers(): Promise<TierAccess[]> {
  const data = await requestJson<{ tiers: TierAccess[] }>("GET", "/api/docs/tiers");
  return data.tiers;
}

/**
 * List blobs in a tier under an optional virtual-folder prefix. With `since`
 * (an ISO-8601 timestamp, E2) only blobs modified strictly after it are
 * returned — the cheap changed-since poll. Each `DocBlob` carries `etag` +
 * `last_modified`, so the caller can advance its high-water mark.
 */
export async function listDocs(
  tier: string,
  prefix = "",
  since?: string,
): Promise<DocBlob[]> {
  const params = new URLSearchParams();
  if (prefix) params.set("prefix", prefix);
  if (since) params.set("since", since);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const data = await requestJson<{ blobs: DocBlob[] }>(
    "GET",
    `/api/docs/${encodeURIComponent(tier)}/list${qs}`,
  );
  return data.blobs;
}

/** Download a document's bytes (streamed by the BFF). */
export async function downloadDoc(tier: string, path: string): Promise<Blob> {
  return (await downloadDocWithEtag(tier, path)).blob;
}

/**
 * Download a document's bytes *plus* its etag (E1) — the etag lets a caller
 * seed the next conditional overwrite's `If-Match`. `etag` is `null` when the
 * BFF didn't expose one (older backend / CORS not exposing the header).
 */
export async function downloadDocWithEtag(
  tier: string,
  path: string,
): Promise<{ blob: Blob; etag: string | null }> {
  const res = await fetch(
    docsUrl(`/api/docs/${encodeURIComponent(tier)}/file?path=${encodeURIComponent(path)}`),
    { headers: await authHeader() },
  );
  if (!res.ok) throw await errorFrom(res);
  return { blob: await res.blob(), etag: res.headers.get("ETag") };
}

/**
 * Optimistic-concurrency precondition for a write (E1):
 *   - `{ ifMatch }`     → conditional overwrite; 412 if the etag is stale.
 *   - `{ createOnly }`  → create-only (`If-None-Match: *`); 412 if it exists.
 *   - neither / omitted → blind overwrite (the binary file plane default).
 */
export interface WritePrecondition {
  ifMatch?: string | null;
  createOnly?: boolean;
}

function preconditionHeaders(opts?: WritePrecondition): Record<string, string> {
  if (opts?.ifMatch) return { "If-Match": opts.ifMatch };
  if (opts?.createOnly) return { "If-None-Match": "*" };
  return {};
}

/**
 * Upload a document. `path` is the virtual blob path within the tier. Without
 * `opts` it's a blind overwrite (binary file plane). Pass `opts` for the E1
 * conditional write; the resolved new etag is returned (`null` if the BFF
 * didn't surface one) so a conditional caller can seed its next `If-Match`.
 */
export async function uploadDoc(
  tier: string,
  path: string,
  file: Blob,
  opts?: WritePrecondition,
): Promise<{ etag: string | null }> {
  const res = await fetch(
    docsUrl(`/api/docs/${encodeURIComponent(tier)}/file?path=${encodeURIComponent(path)}`),
    {
      method: "PUT",
      headers: {
        ...(await authHeader()),
        "Content-Type": file.type || "application/octet-stream",
        ...preconditionHeaders(opts),
      },
      body: file,
    },
  );
  if (!res.ok) throw await errorFrom(res);
  return { etag: res.headers.get("ETag") };
}

/** Delete a document. */
export async function deleteDoc(tier: string, path: string): Promise<void> {
  await requestJson(
    "DELETE",
    `/api/docs/${encodeURIComponent(tier)}/file?path=${encodeURIComponent(path)}`,
  );
}

/**
 * Substring-match blob basenames/paths across readable tiers.
 * Covers Library files that browse shows but Qdrant discovery misses
 * (underscore stems, not-yet-ingested blobs, queries without extension).
 */
async function inventoryFilenameHits(query: string): Promise<SearchHit[]> {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];

  let tiers: TierAccess[];
  try {
    tiers = await listTiers();
  } catch {
    return [];
  }

  const hits: SearchHit[] = [];
  const seen = new Set<string>();

  await Promise.all(
    tiers.map(async (t) => {
      let blobs: DocBlob[];
      try {
        blobs = await listDocs(t.tier);
      } catch {
        return;
      }
      for (const b of blobs) {
        const name = b.path.split("/").pop() ?? b.path;
        const hay = `${name}\0${b.path}`.toLowerCase();
        if (!hay.includes(q)) continue;
        const key = `${t.tier}:${b.path}`;
        if (seen.has(key)) continue;
        seen.add(key);
        hits.push({
          content: `Matched filename in ${t.tier}`,
          context_tag: name,
          filename: name,
          page: "",
          score: 1,
          filepath: b.path,
          classification: t.tier,
        });
      }
    }),
  );

  return hits;
}

function hitDedupeKey(hit: SearchHit): string {
  const loc = resolveDocHitLocation(hit);
  if (loc) return `${loc.tier}:${loc.path}`;
  return `fn:${(hit.filename || hit.context_tag || "").toLowerCase()}`;
}

/** AI search across the tiers the caller may read (BFF computes the boundary). */
export async function searchDocs(query: string, topK = 5): Promise<SearchResponse> {
  const [rag, inventory] = await Promise.all([
    requestJson<SearchResponse>("POST", "/api/search", {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, top_k: topK }),
    }).catch((err: unknown) => {
      // Inventory alone can still answer filename queries when RAG is down.
      if (err instanceof DocsApiError && err.status === 503) {
        return { results: [] as SearchHit[], collections_searched: [] as string[] };
      }
      throw err;
    }),
    inventoryFilenameHits(query),
  ]);

  const merged: SearchHit[] = [];
  const seen = new Set<string>();
  // Filename inventory first — matches what Library browse shows.
  for (const hit of [...inventory, ...rag.results]) {
    const key = hitDedupeKey(hit);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(hit);
  }

  return {
    results: merged.slice(0, Math.max(topK, inventory.length)),
    collections_searched: rag.collections_searched,
  };
}

/* ── Multi-client facilitation surface (multi-client plan) ─────────────── */

/** A registered client (the picker source). Facilitation-side only — the
 *  BFF 403s client users, and the UI never calls this for them. */
export interface ClientEntry {
  id: string;
  name: string;
  status: string;
  /** Optional mark URL when the registry grows a logo field. */
  logoUrl?: string | null;
}

export async function listClients(): Promise<ClientEntry[]> {
  const data = await requestJson<{ clients: ClientEntry[] }>("GET", "/api/clients");
  return data.clients;
}

/** Seed user at client onboard — real mailbox + display names.
 *  Role keys match ZITADEL project roles (never MISSION_FACILITATOR). */
export interface OnboardUserSpec {
  email: string;
  givenName: string;
  familyName?: string;
  role: string;
}

export interface OnboardCredential {
  email: string;
  password: string;
}

export interface CreateClientResult {
  client: ClientEntry;
  credentials?: OnboardCredential[];
}

/** Facilitator-only: create ZITADEL org + project grant + optional users,
 *  then append the registry. Temp passwords are returned once. */
export async function createClient(input: {
  id: string;
  name: string;
  users?: OnboardUserSpec[];
}): Promise<CreateClientResult> {
  return await requestJson<CreateClientResult>("POST", "/api/clients", {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

/** Soft-deactivate or reactivate a registered client (registry status only). */
export async function patchClientStatus(
  clientId: string,
  status: "active" | "inactive",
): Promise<ClientEntry> {
  const data = await requestJson<{ client: ClientEntry }>(
    "PATCH",
    `/api/clients/${encodeURIComponent(clientId)}`,
    {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    },
  );
  return data.client;
}

export interface ClientUserCredential {
  email: string;
  password?: string;
  status: string;
}

/** Add one user to an existing active client org (ZITADEL via BFF PAT). */
export async function createClientUser(
  clientId: string,
  input: {
    email: string;
    givenName: string;
    familyName?: string;
    role: string;
    password?: string;
  },
): Promise<ClientUserCredential> {
  const data = await requestJson<{ credential: ClientUserCredential }>(
    "POST",
    `/api/clients/${encodeURIComponent(clientId)}/users`,
    {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return data.credential;
}

/** One entry in a client's share manifest: exactly one of path | prefix. */
export interface ShareEntry {
  tier: string;
  path?: string;
  prefix?: string;
}

export interface ShareManifest {
  version: number;
  client_id: string;
  shares: ShareEntry[];
  updated_by?: string;
  updated_at?: string;
}

/** Read a client's share manifest (BFF returns an empty default if absent). */
export async function getShareManifest(clientId: string): Promise<ShareManifest> {
  return await requestJson<ShareManifest>(
    "GET",
    `/api/clients/${encodeURIComponent(clientId)}/share`,
  );
}

/** Replace a client's share manifest. Facilitator-only; strictly validated
 *  server-side (any malformed entry → 400, nothing saved). */
export async function putShareManifest(
  clientId: string,
  shares: ShareEntry[],
): Promise<ShareManifest> {
  return await requestJson<ShareManifest>(
    "PUT",
    `/api/clients/${encodeURIComponent(clientId)}/share`,
    {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shares }),
    },
  );
}

/* ── JSON document helpers ────────────────────────────────────────────────
 *
 * Workshop records ride the generic blob endpoints as `application/json`
 * blobs (no dedicated endpoint — see the "workshop records as JSON on the
 * blob plane" ADR). These thin wrappers over uploadDoc/downloadDoc give
 * stores a typed get/put. `getJson` returns `null` on 404 (a never-written
 * record) so first-run hydration sees "empty", matching the soft-miss
 * convention the GitHub adapter had — `downloadDoc` itself throws on 404.
 */

/** GET + parse a JSON document. `null` when it doesn't exist (404). */
export async function getJson<T>(tier: string, path: string): Promise<T | null> {
  return (await getJsonWithEtag<T>(tier, path)).value;
}

/**
 * GET + parse a JSON document *with* its etag (E1). `value` is `null` and
 * `etag` is `null` when the document doesn't exist yet (404) — the signal a
 * conditional caller uses to switch to a create-only first write.
 */
export async function getJsonWithEtag<T>(
  tier: string,
  path: string,
): Promise<{ value: T | null; etag: string | null }> {
  try {
    const { blob, etag } = await downloadDocWithEtag(tier, path);
    const text = await blob.text();
    return { value: text ? (JSON.parse(text) as T) : null, etag };
  } catch (err) {
    if (err instanceof DocsApiError && err.status === 404) {
      return { value: null, etag: null };
    }
    throw err;
  }
}

/** PUT a value as a JSON document — unconditional blind overwrite. Prefer
 *  {@link putJsonWithEtag} for the E1 optimistic-concurrency path; this remains
 *  for callers that genuinely want last-write-wins. */
export async function putJson(tier: string, path: string, value: unknown): Promise<void> {
  await putJsonWithEtag(tier, path, value);
}

/**
 * PUT a JSON document under an optimistic-concurrency precondition (E1) and
 * return the new etag. Pass `{ ifMatch }` to overwrite only if unchanged, or
 * `{ createOnly: true }` for a first write that must not clobber. A stale etag
 * / existing-blob conflict throws {@link DocsConflictError} (HTTP 412).
 */
export async function putJsonWithEtag(
  tier: string,
  path: string,
  value: unknown,
  opts?: WritePrecondition,
): Promise<{ etag: string | null }> {
  const blob = new Blob([JSON.stringify(value)], { type: "application/json" });
  return await uploadDoc(tier, path, blob, opts);
}
